import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { Link, useLocation } from "react-router-dom";
import { NAV_ICON_SIZE, rowPaddingFor } from "./styles";
import { pathMatches, type NavItem } from "./navItems";

// Child rows line their text up with the parent's label; a hairline marks the group.
const CHILD_INDENT = `calc(${rowPaddingFor(NAV_ICON_SIZE)} + ${NAV_ICON_SIZE}px + 12px)`;
const GUIDE_LEFT = `calc(${rowPaddingFor(NAV_ICON_SIZE)} + ${NAV_ICON_SIZE / 2}px)`;

/**
 * A child row lights up on its own page and, unless it is the section's
 * overview (which shares the parent's path), on its sub-pages too, so a
 * collection's page keeps "Collections" highlighted.
 */
function isChildSelected(pathname: string, childTo: string, hasOwnSubtree: boolean): boolean {
  return pathname === childTo || (hasOwnSubtree && pathname.startsWith(`${childTo}/`));
}

interface SidebarNavItemProps {
  item: NavItem;
  /** Link target; defaults to `item.path` (tenant pages pass the tenant-prefixed path). */
  to?: string;
  /** Light up only on `to` itself, not its sub-pages. */
  exact?: boolean;
  collapsed: boolean;
  /** Runs an action item (`item.action`): it opens something instead of linking. */
  onAction?: (action: NonNullable<NavItem["action"]>) => void;
}

export default function SidebarNavItem({
  item,
  to = item.path,
  exact = false,
  collapsed,
  onAction,
}: SidebarNavItemProps) {
  const { pathname } = useLocation();
  const toPath = to.split("?")[0];
  const action = item.action;
  const isSelected = !action && (exact ? pathname === toPath : pathMatches(pathname, toPath));

  const button = action ? (
    <ListItemButton onClick={() => onAction?.(action)}>
      <ListItemIcon>{item.icon}</ListItemIcon>
      {!collapsed && <ListItemText primary={item.label} />}
    </ListItemButton>
  ) : (
    <ListItemButton
      component={Link}
      to={to}
      selected={isSelected}
      sx={item.secondary ? { "--row-py": "6px" } : undefined}
    >
      <ListItemIcon>{item.icon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          secondary={item.secondary}
          slotProps={{
            primary: { noWrap: true },
            secondary: {
              noWrap: true,
              sx: { fontSize: "0.6875rem", lineHeight: 1.35, color: "text.secondary" },
            },
          }}
        />
      )}
    </ListItemButton>
  );

  if (collapsed) {
    return (
      <Tooltip title={item.label} placement="right" disableInteractive>
        {button}
      </Tooltip>
    );
  }
  if (!item.children) return button;

  // The section's pages, open while one of them is shown. The parent links
  // to the first (its landing page).
  const base = to.slice(0, to.length - item.path.length);
  return (
    <Box>
      {button}
      <Collapse in={isSelected}>
        <Box
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 2,
              bottom: 4,
              left: `calc(8px + ${GUIDE_LEFT})`,
              width: "1px",
              bgcolor: "divider",
            },
          }}
        >
          {item.children.map((child) => {
            const childTo = `${base}${child.path}`;
            return (
              <ListItemButton
                key={childTo}
                component={Link}
                to={childTo}
                selected={isChildSelected(pathname, childTo, child.path !== item.path)}
                sx={{ "--row-pl": CHILD_INDENT, "--row-min-height": "30px" }}
              >
                <ListItemText primary={child.label} />
              </ListItemButton>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
